import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layers, X, Mail, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import GoogleLoginButton from '../auth/GoogleLoginButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REMEMBER_KEY = 'hud_remember_session';

const GoogleSvg = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

/**
 * AuthOverlay — modale glassmorphism cockpit de connexion.
 *
 * S'affiche en overlay par-dessus la carte Landing (sans changement de route)
 * pour limiter le sentiment de changement d'interface.
 *
 * Affichage automatique :
 *   - quand l'URL contient ?auth=1
 *   - quand un user non connecté tente d'accéder à une route protégée
 *     (ProtectedRoute redirige vers /?auth=1&next=...)
 *
 * Props:
 *   - open: bool (controlled) — si non fourni, déduit de la query ?auth=1
 *   - onClose: () => void
 *   - nextPath: string — route à rejoindre après auth (override ?next=)
 */
const AuthOverlay = ({ open: openProp, onClose, nextPath }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, login: emergentLogin } = useAuth();

  const params = new URLSearchParams(location.search);
  const queryOpen = params.get('auth') === '1';
  const queryNext = params.get('next') || '/studio/dashboard';
  const open = openProp !== undefined ? openProp : queryOpen;
  const next = nextPath || queryNext;

  const [authConfig, setAuthConfig] = useState({ emergent_oauth: true, google_direct: false, allowed_email_domains: [] });
  const [remember, setRemember] = useState(true);

  // Restore remember-me preference
  useEffect(() => {
    try { setRemember(localStorage.getItem(REMEMBER_KEY) !== '0'); } catch {}
  }, []);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/auth/config`).then(r => setAuthConfig(r.data)).catch(() => {});
  }, [open]);

  // Auto-close si user est déjà loggé : redirige vers next
  useEffect(() => {
    if (open && user) {
      navigate(next, { replace: true });
    }
  }, [open, user, next, navigate]);

  // ESC fermeture
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const persistRemember = (val) => {
    setRemember(val);
    try { localStorage.setItem(REMEMBER_KEY, val ? '1' : '0'); } catch {}
  };

  const handleClose = () => {
    if (onClose) return onClose();
    // Strip ?auth=1&next=... from URL without reload
    const url = new URL(window.location.href);
    url.searchParams.delete('auth');
    url.searchParams.delete('next');
    navigate(url.pathname + (url.search || ''), { replace: true });
  };

  const handleEmergentLogin = () => {
    persistRemember(remember);
    // Redirect URL = next (so on retour OAuth, atterrit directement sur la route demandée)
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + (next.startsWith('/') ? next : '/studio/dashboard');
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleRequestAccess = () => {
    window.open('mailto:hello@shoebox3d.com?subject=Demande%20d%27acc%C3%A8s%20Peter%27s%20Universe', '_self');
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4 hud-overlay-backdrop"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}
      data-testid="auth-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-overlay-title"
      onClick={handleClose}
    >
      {/* Modal */}
      <div
        className="hud-overlay-modal relative w-full max-w-[440px] rounded-[24px] overflow-hidden border"
        style={{
          background: 'rgba(10,10,13,0.88)',
          borderColor: 'var(--c-border-accent)',
          boxShadow: 'var(--shadow-cockpit)',
          backdropFilter: 'blur(28px)',
          WebkitBackdropFilter: 'blur(28px)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Grain overlay subtle */}
        <span
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.07] mix-blend-overlay"
          style={{
            backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
            backgroundSize: '240px 240px',
          }}
        />

        {/* Glow halo top */}
        <span
          aria-hidden
          className="absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full pointer-events-none"
          style={{ background: 'rgba(217,160,91,0.18)', filter: 'blur(60px)' }}
        />

        {/* Close */}
        <button
          onClick={handleClose}
          data-testid="auth-overlay-close"
          aria-label="Fermer"
          className="absolute top-3 right-3 w-9 h-9 rounded-lg flex items-center justify-center transition-colors z-10"
          style={{
            background: 'var(--c-surface-2)',
            border: '1px solid var(--c-border)',
            color: 'var(--c-text-muted)',
          }}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative z-[1] p-8 sm:p-10">
          {/* Header */}
          <div className="flex flex-col items-center text-center mb-7">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: 'var(--c-gold)', boxShadow: 'var(--shadow-glow)' }}
            >
              <Layers className="w-6 h-6 text-black" strokeWidth={2} />
            </div>
            <span className="hud-eyebrow hud-eyebrow-gold mb-2">Accès cockpit</span>
            <h2
              id="auth-overlay-title"
              className="font-semibold tracking-tight leading-none"
              style={{
                fontFamily: 'Outfit, sans-serif',
                color: 'var(--c-text)',
                fontSize: '26px',
              }}
            >
              Bienvenue
            </h2>
            <p className="mt-2 text-[13.5px]" style={{ color: 'var(--c-text-2)' }}>
              Connectez-vous pour rejoindre votre univers Peter's
            </p>
          </div>

          {/* Primary CTA — Google direct popup (production)
              Fallback to Emergent proxy if direct flow is disabled (dev). */}
          <GoogleLoginButton
            next={next}
            dataTestId="auth-overlay-google-btn"
            className="w-full flex items-center justify-center gap-3 rounded-xl px-4 h-12 font-semibold transition-all hover:bg-[#F4F4F5]"
            onSuccess={() => {
              persistRemember(remember);
              navigate(next, { replace: true });
            }}
            fallback={
              <button
                onClick={handleEmergentLogin}
                data-testid="auth-overlay-google-btn"
                className="w-full flex items-center justify-center gap-3 rounded-xl px-4 h-12 font-semibold transition-all"
                style={{
                  background: '#FFFFFF',
                  color: '#18181B',
                  boxShadow: '0 0 0 1px rgba(255,255,255,0.08), var(--shadow-glow)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#F4F4F5'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; }}
              >
                <GoogleSvg />
                Continuer avec Google
              </button>
            }
          >
            <span style={{ background: '#FFFFFF', color: '#18181B', boxShadow: '0 0 0 1px rgba(255,255,255,0.08), var(--shadow-glow)' }} className="w-full h-12 rounded-xl flex items-center justify-center gap-3 font-semibold">
              <GoogleSvg />
              Continuer avec Google
            </span>
          </GoogleLoginButton>

          {/* Optional Direct Google (when configured) */}
          {/* Legacy block removed — the primary Google button above is now
              the direct popup OAuth (no Emergent proxy in production). */}

          {/* Remember me */}
          <label
            className="flex items-center gap-2.5 mt-4 cursor-pointer select-none"
            data-testid="auth-overlay-remember-label"
          >
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => persistRemember(e.target.checked)}
              data-testid="auth-overlay-remember-checkbox"
              className="w-4 h-4 rounded accent-[#D9A05B] cursor-pointer"
              style={{ accentColor: 'var(--c-gold)' }}
            />
            <span className="text-[13px]" style={{ color: 'var(--c-text-2)' }}>
              Rester connecté <span style={{ color: 'var(--c-text-muted)' }}>· 30 jours</span>
            </span>
          </label>

          {/* B2B email allowlist notice */}
          {authConfig.allowed_email_domains?.length > 0 && (
            <div
              className="mt-4 px-3 py-2.5 rounded-lg text-[12px] flex items-start gap-2"
              style={{
                background: 'rgba(234,179,8,0.08)',
                border: '1px solid rgba(234,179,8,0.22)',
                color: '#FDE68A',
              }}
              data-testid="email-allowlist-notice"
            >
              <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Accès réservé aux domaines : <strong>{authConfig.allowed_email_domains.join(', ')}</strong>
              </span>
            </div>
          )}

          {/* Secondary — request access */}
          <button
            onClick={handleRequestAccess}
            data-testid="auth-overlay-request-access"
            className="hud-btn hud-btn-ghost hud-btn-md w-full mt-4"
          >
            <Mail className="w-4 h-4" />
            Demander un accès
          </button>

          {/* Footer */}
          <p
            className="mt-6 text-center text-[11px]"
            style={{ color: 'var(--c-text-faint)' }}
          >
            En continuant, vous acceptez nos conditions d'utilisation
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthOverlay;
export { AuthOverlay };
